import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, Plus, Trash2, Edit, Shield, Settings, Key, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EnterpriseSettings() {
  const [activeTab, setActiveTab] = useState('roles');
  const [loading, setLoading] = useState(false);

  // Roles state
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', permissions: [] });

  // Integrations state
  const [integrations, setIntegrations] = useState([]);
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [integrationForm, setIntegrationForm] = useState({ name: '', type: 'webhook', config: {}, is_active: true });

  // Platform Config state
  const [platformConfig, setPlatformConfig] = useState({
    base_delivery_fee: 5.00,
    per_km_rate: 1.50,
    platform_commission_percent: 15.0,
    max_delivery_distance_km: 25.0,
    min_order_value: 10.00,
    currency: 'USD',
    tax_rate_percent: 8.0
  });
  const [platformConfigLoading, setPlatformConfigLoading] = useState(false);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilters, setAuditFilters] = useState({ entity_type: '', action: '' });

  useEffect(() => {
    if (activeTab === 'roles') {
      fetchRoles();
      fetchPermissions();
    } else if (activeTab === 'integrations') {
      fetchIntegrations();
    } else if (activeTab === 'platform') {
      fetchPlatformConfig();
    } else if (activeTab === 'audit') {
      fetchAuditLogs();
    }
  }, [activeTab, auditPage, auditFilters]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  // ============= ROLES & PERMISSIONS =============

  const fetchRoles = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/enterprise-settings/roles`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setRoles(data.roles || []);
    } catch (error) {
      toast.error('Failed to fetch roles');
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/enterprise-settings/permissions`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setPermissions(data.permissions || []);
    } catch (error) {
      toast.error('Failed to fetch permissions');
    }
  };

  const handleCreateRole = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/admin/enterprise-settings/roles`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(roleForm)
      });

      if (res.ok) {
        toast.success('Role created successfully');
        setRoleDialogOpen(false);
        setRoleForm({ name: '', description: '', permissions: [] });
        fetchRoles();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create role');
      }
    } catch (error) {
      toast.error('Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/admin/enterprise-settings/roles/${editingRole.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(roleForm)
      });

      if (res.ok) {
        toast.success('Role updated successfully');
        setRoleDialogOpen(false);
        setEditingRole(null);
        setRoleForm({ name: '', description: '', permissions: [] });
        fetchRoles();
      } else {
        toast.error('Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/enterprise-settings/roles/${roleId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('Role deleted');
        fetchRoles();
      } else {
        toast.error('Failed to delete role');
      }
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  // ============= INTEGRATIONS =============

  const fetchIntegrations = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/enterprise-settings/integrations`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setIntegrations(data.integrations || []);
    } catch (error) {
      toast.error('Failed to fetch integrations');
    }
  };

  const handleCreateIntegration = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/admin/enterprise-settings/integrations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(integrationForm)
      });

      if (res.ok) {
        toast.success('Integration created');
        setIntegrationDialogOpen(false);
        setIntegrationForm({ name: '', type: 'webhook', config: {}, is_active: true });
        fetchIntegrations();
      } else {
        toast.error('Failed to create integration');
      }
    } catch (error) {
      toast.error('Failed to create integration');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/enterprise-settings/integrations/${integrationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('Integration deleted');
        fetchIntegrations();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // ============= PLATFORM CONFIG =============

  const fetchPlatformConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/platform-config`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setPlatformConfig(data);
    } catch (error) {
      toast.error('Failed to fetch platform config');
    }
  };

  const handleSavePlatformConfig = async () => {
    try {
      setPlatformConfigLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/admin/platform-config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(platformConfig)
      });

      if (res.ok) {
        toast.success('Platform configuration updated');
        fetchPlatformConfig();
      } else {
        toast.error('Failed to update configuration');
      }
    } catch (error) {
      toast.error('Failed to update configuration');
    } finally {
      setPlatformConfigLoading(false);
    }
  };

  // ============= AUDIT LOGS =============

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: auditPage,
        limit: 20,
        ...(auditFilters.entity_type && { entity_type: auditFilters.entity_type }),
        ...(auditFilters.action && { action: auditFilters.action })
      });

      const res = await fetch(`${BACKEND_URL}/api/admin/enterprise-settings/audit-logs?${params}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setAuditLogs(data.logs || []);
      setAuditTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleExportAuditLogs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/enterprise-settings/audit-logs/export?format=csv`, {
        headers: getAuthHeaders()
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString()}.csv`;
        a.click();
        toast.success('Audit logs exported');
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Group permissions by category
  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {});

  return (
    <div className="p-8 space-y-6" data-testid="enterprise-settings-page">
      <div>
        <h1 className="text-3xl font-bold" data-font="display">Enterprise Settings</h1>
        <p className="text-muted-foreground">Manage roles, permissions, integrations, and platform configuration</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="roles" data-testid="roles-tab">
            <Shield className="h-4 w-4 mr-2" />
            Roles & Permissions
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="integrations-tab">
            <Key className="h-4 w-4 mr-2" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="platform" data-testid="platform-tab">
            <Settings className="h-4 w-4 mr-2" />
            Platform Config
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="audit-tab">
            <FileText className="h-4 w-4 mr-2" />
            Audit Logs
          </TabsTrigger>
        </TabsList>

        {/* ROLES TAB */}
        <TabsContent value="roles" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">{roles.length} roles</p>
            <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingRole(null); setRoleForm({ name: '', description: '', permissions: [] }); }} data-testid="create-role-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Role
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
                  <DialogDescription>Define role name, description, and permissions</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="role-name">Role Name</Label>
                    <Input
                      id="role-name"
                      value={roleForm.name}
                      onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                      placeholder="e.g., Support Manager"
                      data-testid="role-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="role-desc">Description</Label>
                    <Input
                      id="role-desc"
                      value={roleForm.description}
                      onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                      placeholder="Role description"
                    />
                  </div>
                  <div>
                    <Label>Permissions</Label>
                    <div className="border rounded-md p-4 max-h-64 overflow-y-auto space-y-4">
                      {Object.entries(groupedPermissions).map(([category, perms]) => (
                        <div key={category}>
                          <h4 className="font-semibold text-sm mb-2 capitalize">{category}</h4>
                          <div className="space-y-2">
                            {perms.map((perm) => (
                              <div key={perm.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={perm.id}
                                  checked={roleForm.permissions.includes(perm.id)}
                                  onCheckedChange={() => handlePermissionToggle(perm.id)}
                                />
                                <Label htmlFor={perm.id} className="text-sm cursor-pointer">{perm.name}</Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
                  <Button onClick={editingRole ? handleUpdateRole : handleCreateRole} disabled={loading} data-testid="save-role-btn">
                    {loading ? 'Saving...' : 'Save Role'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium">{role.name}</TableCell>
                    <TableCell className="text-muted-foreground">{role.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{role.permissions?.length || 0} permissions</Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingRole(role);
                          setRoleForm({ name: role.name, description: role.description, permissions: role.permissions || [] });
                          setRoleDialogOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteRole(role.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {roles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No roles created yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* INTEGRATIONS TAB */}
        <TabsContent value="integrations" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">{integrations.length} integrations</p>
            <Dialog open={integrationDialogOpen} onOpenChange={setIntegrationDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setIntegrationForm({ name: '', type: 'webhook', config: {}, is_active: true })}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Integration
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Integration</DialogTitle>
                  <DialogDescription>Configure external service integration</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Integration Name</Label>
                    <Input
                      value={integrationForm.name}
                      onChange={(e) => setIntegrationForm({ ...integrationForm, name: e.target.value })}
                      placeholder="e.g., Stripe Payments"
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={integrationForm.type} onValueChange={(v) => setIntegrationForm({ ...integrationForm, type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="webhook">Webhook</SelectItem>
                        <SelectItem value="api">API</SelectItem>
                        <SelectItem value="oauth">OAuth</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>API Key / Config</Label>
                    <Input
                      type="password"
                      placeholder="API Key or configuration"
                      onChange={(e) => setIntegrationForm({ ...integrationForm, config: { api_key: e.target.value } })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIntegrationDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreateIntegration} disabled={loading}>Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {integrations.map((integration) => (
              <Card key={integration.id}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {integration.name}
                      {integration.is_active ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-50 text-gray-700">Inactive</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>{integration.type}</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteIntegration(integration.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </CardHeader>
              </Card>
            ))}
            {integrations.length === 0 && (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No integrations configured
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* PLATFORM CONFIG TAB */}
        <TabsContent value="platform" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Platform Configuration</CardTitle>
              <CardDescription>Configure delivery fees, zones, and business rules</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Base Delivery Fee</Label>
                  <Input type="number" placeholder="5.00" />
                </div>
                <div>
                  <Label>Per KM Rate</Label>
                  <Input type="number" placeholder="1.50" />
                </div>
                <div>
                  <Label>Platform Commission (%)</Label>
                  <Input type="number" placeholder="15" />
                </div>
                <div>
                  <Label>Max Delivery Distance (km)</Label>
                  <Input type="number" placeholder="25" />
                </div>
              </div>
              <Button className="mt-4">Save Configuration</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AUDIT LOGS TAB */}
        <TabsContent value="audit" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Select value={auditFilters.entity_type || "all"} onValueChange={(v) => setAuditFilters({ ...auditFilters, entity_type: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Entity Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                  <SelectItem value="order">Order</SelectItem>
                  <SelectItem value="role">Role</SelectItem>
                  <SelectItem value="integration">Integration</SelectItem>
                </SelectContent>
              </Select>
              <Select value={auditFilters.action || "all"} onValueChange={(v) => setAuditFilters({ ...auditFilters, action: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleExportAuditLogs} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Entity ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{log.actor_name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">{log.actor_email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{log.action}</Badge>
                    </TableCell>
                    <TableCell className="capitalize">{log.entity_type}</TableCell>
                    <TableCell className="font-mono text-xs">{log.entity_id}</TableCell>
                  </TableRow>
                ))}
                {auditLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      {loading ? 'Loading...' : 'No audit logs found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>

          {auditTotal > 20 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => setAuditPage(p => Math.max(1, p - 1))}
                disabled={auditPage === 1}
              >
                Previous
              </Button>
              <span className="py-2 px-4">Page {auditPage}</span>
              <Button
                variant="outline"
                onClick={() => setAuditPage(p => p + 1)}
                disabled={auditPage * 20 >= auditTotal}
              >
                Next
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
